import type {
  BracketBackgroundFit,
  BracketBackgroundPosition,
  BracketBackgroundPreset,
  BracketBackgroundSettings,
  BracketTheme,
  BracketThemeOverride,
  WinnerSide,
} from './types'

export const QUICK_COUNTS = [8, 16, 32, 64, 128]
export const MIN_TEAMS = 2
export const MAX_TEAMS = 128
export const DEFAULT_EMPTY_TEAMS = 8
export const MIN_ZOOM = 0.2
export const MAX_ZOOM = 2
export const BRACKET_ROW_HEIGHT = 140
export const BRACKET_ROW_GAP = 48
export const BRACKET_COLUMN_WIDTH = 260
export const BRACKET_COLUMN_GAP = 64
export const FINAL_COLUMN_WIDTH = 400
export const SINGLE_FINAL_COLUMN_WIDTH = BRACKET_COLUMN_WIDTH
export const CHAMPION_COLUMN_WIDTH = 220
export const CHAMPION_CONNECTOR_OFFSET = 0
export const HORIZONTAL_FINAL_SLOT_EDGE_OFFSET = 16
export const BRACKET_TOP_PADDING = 144
export const VERTICAL_MIN_BRACKET_WIDTH = 520
export const VERTICAL_HEADER_RAIL_WIDTH = 184
export const VERTICAL_HEADER_GAP = 24
export const VERTICAL_SLOT_EDGE_OFFSET = 72
export const VERTICAL_STACKED_FINAL_CONNECTOR_GAP = 0
export const REGION_GAP = 48
export const IMAGE_SLOT_SIZE = 112
export const IMAGE_CENTER_OFFSET = 16
export const TEXT_SLOT_WIDTH = 144
export const TEXT_SLOT_HEIGHT = 64
export const SLOT_ROW_GAP_ALIGNMENT_OFFSET = BRACKET_ROW_GAP / 4
export const VERTICAL_SLOT_LEFT_OVERFLOW = Math.ceil(
  Math.max(
    0,
    TEXT_SLOT_WIDTH / 2 -
      (BRACKET_ROW_HEIGHT * 0.25 - SLOT_ROW_GAP_ALIGNMENT_OFFSET),
    IMAGE_SLOT_SIZE / 2 -
      (BRACKET_ROW_HEIGHT * 0.25 - SLOT_ROW_GAP_ALIGNMENT_OFFSET),
  ),
)
export const VERTICAL_HEADER_CONTENT_GAP =
  VERTICAL_HEADER_GAP + VERTICAL_SLOT_LEFT_OVERFLOW
export const DEFAULT_TEAM_IMAGES = ['/team/face.webp', '/team/face2.webp']
export const DEFAULT_TEAM_IMAGE = DEFAULT_TEAM_IMAGES[0]
export const BRACKET_BACKGROUND_STORAGE_KEY = 'snapbracket:bracket-background'
export const BRACKET_BACKGROUND_FIT_OPTIONS: BracketBackgroundFit[] = [
  'cover',
  'contain',
  'tile',
]
export const BRACKET_BACKGROUND_POSITION_OPTIONS: BracketBackgroundPosition[] =
  ['center', 'top', 'bottom']
export const BRACKET_BACKGROUND_PRESETS: BracketBackgroundPreset[] = [
  {
    id: 'stadium',
    image: '/bracket-backgrounds/stadium-lights.jpg',
    defaultOpacity: 0.62,
    defaultOverlayOpacity: 0.42,
    defaultOverlayTone: 'light',
  },
  {
    id: 'basketball',
    image: '/bracket-backgrounds/basketball-court.jpg',
    defaultOpacity: 0.56,
    defaultOverlayOpacity: 0.48,
    defaultOverlayTone: 'light',
  },
  {
    id: 'paper',
    image: '/bracket-backgrounds/paper-bracket.jpg',
    defaultOpacity: 0.72,
    defaultOverlayOpacity: 0.16,
    defaultOverlayTone: 'light',
  },
  {
    id: 'esports',
    image: '/bracket-backgrounds/esports-stage.jpg',
    defaultOpacity: 0.66,
    defaultOverlayOpacity: 0.42,
    defaultOverlayTone: 'light',
  },
]
export const DEFAULT_BRACKET_BACKGROUND_SETTINGS: BracketBackgroundSettings = {
  preset: 'none',
  customImage: null,
  fit: 'cover',
  position: 'center',
  opacity: 0.64,
  overlayOpacity: 0.42,
  overlayTone: 'light',
  blur: 0,
  print: false,
}
export const DEFAULT_BRACKET_THEME: BracketTheme = {
  actionIconButton: 'h-8 w-8',
  canvasActionBar: 'flex items-center justify-end gap-1',
  canvasContainer:
    'relative min-h-[640px] overflow-hidden bg-gradient-to-br from-background via-background to-background/80 print:h-auto print:overflow-visible',
  canvasTitleButton:
    'group flex w-full min-w-0 items-center justify-center gap-2 rounded-md px-2 py-1 text-base font-semibold leading-tight text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:w-auto',
  canvasTitleInput:
    'w-full min-w-0 max-w-[260px] rounded-md border border-border bg-background px-3 py-1.5 text-center text-base font-semibold leading-tight shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 sm:w-[200px] sm:max-w-[240px]',
  canvasToolbar:
    'flex w-full flex-col gap-2 border-b bg-inherit px-3 py-2.5 text-sm md:flex-row md:items-center md:justify-between md:gap-4 md:px-4 md:py-3',
  championBadge:
    'rounded-full bg-background px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary',
  championCard:
    'flex w-full max-w-[260px] flex-col items-center gap-3 rounded-lg bg-primary/5 px-4 py-6 text-center ring-1 ring-primary/30',
  controlPanel: 'h-full space-y-6 border bg-card p-4',
  controlSection: 'flex flex-col items-start gap-2',
  controlTitle: 'text-sm font-semibold',
  connector: 'text-foreground/55 dark:text-foreground/50',
  finalMatchCard:
    'relative isolate flex w-full flex-col items-center gap-3 overflow-hidden bg-primary/5 px-3 py-8 ring-1 ring-primary/30',
  finalMatchOverlay: 'pointer-events-none absolute inset-0 bg-primary/5',
  finalVsBadge:
    'h-12 w-12 rounded-full bg-background px-2 py-0.5 text-center text-sm font-semibold uppercase leading-[48px] tracking-wide text-muted-foreground ring-1 ring-border/60',
  gridMatch: 'relative flex h-full flex-col overflow-visible',
  gridMatchFinal: 'relative flex h-full items-center justify-center',
  matchVs: 'select-none text-xs font-bold text-muted-foreground/70',
  nameInput:
    'resize-none border-muted-foreground/20 bg-muted/30 font-mono text-sm focus-visible:ring-primary/20',
  roundHeader:
    'flex items-center justify-between rounded-lg px-3 py-2 shadow-sm ring-1 ring-border/60',
  roundHeaderBadge:
    'rounded-full bg-background px-2 py-0.5 text-[10px] font-semibold text-foreground/80',
  roundHeaderChampion: 'bg-amber-50 text-amber-700',
  roundHeaderFinal: 'bg-primary/10 text-primary',
  roundHeaderStandard: 'bg-secondary/70',
  slotAutoWinBadge:
    'absolute bottom-2 left-2 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-emerald-50 shadow-sm',
  slotBase:
    'relative flex-none overflow-hidden rounded-md border text-left transition-all h-24 w-24 shadow-sm sm:h-28 sm:w-28',
  slotDefault:
    'border-foreground/35 bg-background hover:border-foreground/55 hover:shadow-md dark:border-foreground/30 dark:hover:border-foreground/50',
  slotEmpty:
    'border-dashed border-foreground/30 bg-transparent shadow-none dark:border-foreground/25',
  slotImageOverlay:
    'pointer-events-none absolute inset-0 transition duration-200',
  slotImageOverlayVisible: 'group-hover/slot:bg-black/5',
  slotImageRemoveButton:
    'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-sm bg-background/80 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:bg-destructive hover:text-destructive-foreground group-hover/slot:opacity-100',
  slotName: 'w-full truncate text-sm font-medium text-foreground/80',
  slotNameButton:
    'w-full truncate text-sm font-medium text-foreground transition hover:text-primary',
  slotPlaceholder:
    'flex h-full w-full items-center justify-center bg-muted/20 text-[10px] font-medium text-muted-foreground/60',
  slotRoot: 'group/slot flex w-full flex-col items-center justify-center gap-2',
  slotTextBase: 'h-16 w-36 px-3 py-2 sm:h-16 sm:w-36',
  slotTextInput:
    'w-full rounded-sm border border-foreground/45 bg-background/90 px-2 py-1 text-center text-sm font-semibold leading-tight text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-foreground/40',
  slotTextName:
    'line-clamp-2 w-full text-center text-sm font-semibold leading-tight text-foreground',
  slotTextNameButton:
    'line-clamp-2 w-full rounded-sm px-1 text-center text-sm font-semibold leading-tight text-foreground transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
  slotWinner: 'border-primary bg-primary/5 shadow-md ring-1 ring-primary/20',
  zoomBadge: 'rounded-md bg-muted/50 px-2 py-1 font-medium',
}
export const getDefaultTeamImage = (index: number) =>
  DEFAULT_TEAM_IMAGES[index % DEFAULT_TEAM_IMAGES.length]
export const isDefaultTeamImage = (value?: string | null) =>
  value != null &&
  DEFAULT_TEAM_IMAGES.includes(value as (typeof DEFAULT_TEAM_IMAGES)[number])
export const clampZoomValue = (value: number) =>
  Number(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value)).toFixed(2))
export const getVerticalSlotCenterOffset = (side: WinnerSide) =>
  side === 'home'
    ? -SLOT_ROW_GAP_ALIGNMENT_OFFSET
    : SLOT_ROW_GAP_ALIGNMENT_OFFSET
export const getVerticalSlotImageY = (
  boxTop: number,
  boxHeight: number,
  side: WinnerSide,
) =>
  boxTop +
  boxHeight * (side === 'home' ? 0.25 : 0.75) +
  getVerticalSlotCenterOffset(side) -
  IMAGE_CENTER_OFFSET
export const getGridSpanHeight = (span: number) =>
  span * BRACKET_ROW_HEIGHT + (span - 1) * BRACKET_ROW_GAP
export const createBracketTheme = (
  theme?: BracketThemeOverride,
): BracketTheme => ({
  ...DEFAULT_BRACKET_THEME,
  ...theme,
})
